/**
 * The Settings tab (wireframe 4a) — everything that isn't a language, grouped
 * and calm.
 *
 * Not here yet, on purpose:
 *   - Plan & translation limit — waits for billing (phase 4);
 *   - switching the URL style — the choice returns in phase 2 with the
 *     Address-style screen; until then the style is shown, not editable
 *     (decision M2 27.07 / #66 still stands).
 *
 * Vocabulary rule from the wireframe: no "hostnames", no "DNS zone" — always
 * "language addresses" and "domain records".
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LANGUAGE_LIST } from '@glocal/lang'
import type { ILang, IRoute, IWebsite } from '@glocal/schemas'

import { ConfirmDialog } from '../../components/confirm-dialog'
import { MiniButton } from '../../components/mini-button'
import { trpc } from '../../trpc'
import { checkUrl } from '../../url'
import { bareHost, checkFetchOrigin } from '../wizard/fetch-origin-check'

import { hostOf, langLabel } from './languages'

export function SettingsTab({
	website,
	routes,
	onChanged,
}: {
	website: IWebsite
	routes: IRoute[]
	onChanged: () => void
}) {
	const host = hostOf(website)
	return (
		<div className="flex flex-col gap-4">
			<SiteBasicsCard
				website={website}
				onSaved={onChanged}
			/>
			<UrlStyleCard
				website={website}
				host={host}
			/>
			<DangerCard
				website={website}
				routes={routes}
				host={host}
			/>
		</div>
	)
}

/* ============= Site basics ============= */

function urlErrorMessage(check: ReturnType<typeof checkUrl>): string | null {
	if (check.ok) {
		return null
	}
	switch (check.reason) {
		case 'empty':
			return 'Site address is required.'
		case 'invalid-chars':
			return 'Only letters, digits, dots and hyphens are allowed.'
		case 'missing-tld':
			return "Don't forget the domain zone (.com, .ru, .io…)."
		case 'malformed':
			return 'Site address looks off — try something like example.com.'
	}
}

function SiteBasicsCard({ website, onSaved }: { website: IWebsite; onSaved: () => void }) {
	const [name, setName] = useState(website.name)
	const [origin, setOrigin] = useState(website.origin)
	const [sourceLang, setSourceLang] = useState<ILang>(website.lang)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const dirty = name !== website.name || origin !== website.origin || sourceLang !== website.lang

	const urlCheck = checkUrl(origin)
	const address = addressField(website, origin, urlCheck)
	const urlFieldError = address.error
	const nameFieldError = name.trim().length < 4 && name !== website.name ? 'At least 4 characters.' : null
	const canSave = dirty && urlCheck.ok && !urlFieldError && !nameFieldError && !saving

	async function save() {
		if (!canSave || !urlCheck.ok) {
			return
		}
		setError(null)
		setSaving(true)
		try {
			await trpc().WebsiteService.Update.mutate({
				id: website.id,
				name,
				origin: urlCheck.origin,
				lang: sourceLang,
			})
			onSaved()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to save')
		} finally {
			setSaving(false)
		}
	}

	return (
		<section className="card p-5">
			<h2 className="font-sans text-[16px] font-medium text-ink">Site</h2>

			<div className="mt-4 grid grid-cols-1 gap-4">
				<label className="flex flex-col gap-1.5">
					<span className="field-label">Name</span>
					<input
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						aria-invalid={!!nameFieldError}
						className={
							'field ' +
							(nameFieldError ? 'border-[#B23B2A] focus:border-[#B23B2A] focus:ring-[#B23B2A]/30' : '')
						}
						minLength={4}
					/>
					{nameFieldError && <span className="text-[12px] text-[#B23B2A]">{nameFieldError}</span>}
				</label>

				<label className="flex flex-col gap-1.5">
					<span className="field-label">{address.label}</span>
					<input
						type="text"
						inputMode="url"
						autoComplete="url"
						autoCapitalize="off"
						spellCheck={false}
						value={origin}
						onChange={(e) => setOrigin(e.target.value)}
						aria-invalid={!!urlFieldError}
						className={
							'field ' +
							(urlFieldError ? 'border-[#B23B2A] focus:border-[#B23B2A] focus:ring-[#B23B2A]/30' : '')
						}
						placeholder="example.com"
					/>
					{urlFieldError ? (
						<span className="text-[12px] text-[#B23B2A]">{urlFieldError}</span>
					) : (
						<span className="font-sans text-[12px] text-ink-mute">{address.hint}</span>
					)}
				</label>

				<label className="flex flex-col gap-1.5">
					<span className="field-label">Original language</span>
					<LangDropdown
						value={sourceLang}
						onChange={setSourceLang}
					/>
				</label>
			</div>

			{error && (
				<div className="mt-4 rounded-nayda-sm border border-[#B23B2A]/20 bg-[#B23B2A]/10 px-3 py-2 text-[13px] text-[#B23B2A]">
					{error}
				</div>
			)}

			<div className="mt-5 flex items-center gap-3">
				<button
					onClick={() => void save()}
					disabled={!canSave}
					className="btn"
				>
					{saving ? 'Saving…' : 'Save changes'}
				</button>
				{dirty && !saving && (
					<button
						onClick={() => {
							setName(website.name)
							setOrigin(website.origin)
							setSourceLang(website.lang)
							setError(null)
						}}
						className="font-sans text-[13px] text-ink-soft hover:text-ink"
					>
						Discard
					</button>
				)}
			</div>
		</section>
	)
}

/**
 * Label, hint and error for the address field. A folder site fetched from a
 * hidden origin keeps two addresses apart: the domain visitors use (`public`)
 * and where we fetch pages (`origin`). The editable field is then the fetch
 * address — and it must not be the site's own domain, which points at us, or
 * the proxy would fetch itself.
 */
function addressField(
	website: IWebsite,
	value: string,
	check: ReturnType<typeof checkUrl>
): { label: string; hint: string; error: string | null } {
	const separateFetch = !!website.public && bareHost(website.public) !== bareHost(website.origin)
	if (!separateFetch) {
		return {
			label: 'Site address',
			hint: 'The live source we translate. https:// is added for you.',
			error: urlErrorMessage(check),
		}
	}
	const fetchCheck = checkFetchOrigin(value, website.public ?? '')
	const ownDomain = !fetchCheck.ok && fetchCheck.reason === 'same-as-site'
	return {
		label: 'Address we fetch from',
		hint: `Visitors use ${bareHost(website.public ?? '')}; we fetch its pages from here. https:// is added for you.`,
		error: ownDomain
			? "That's your site's own domain — with folders it points at us, so we fetch from a different address."
			: urlErrorMessage(check),
	}
}

function LangDropdown({ value, onChange }: { value: ILang; onChange: (l: ILang) => void }) {
	const options = (LANGUAGE_LIST as readonly string[])
		.map((l) => ({ code: l as ILang, label: langLabel(l) }))
		.sort((a, b) => a.label.localeCompare(b.label))
	return (
		<select
			value={value}
			onChange={(e) => onChange(e.target.value as ILang)}
			className="field"
		>
			{options.map((o) => (
				<option
					key={o.code}
					value={o.code}
				>
					{o.label} ({o.code.toUpperCase()})
				</option>
			))}
		</select>
	)
}

/* ============= URL style (read-only until phase 2) ============= */

function UrlStyleCard({ website, host }: { website: IWebsite; host: string }) {
	const isSubfolder = website.mode === 'subfolder'
	return (
		<section className="card p-5">
			<h2 className="font-sans text-[16px] font-medium text-ink">How your language addresses look</h2>
			<div className="mt-3 flex flex-wrap gap-2">
				<ModePill
					selected={!isSubfolder}
					label="Subdomains"
					example={`es.${host}`}
				/>
				<ModePill
					selected={isSubfolder}
					label="Folders"
					example={`${host}/es/`}
				/>
			</div>
			<p className="mt-3 text-[12px] text-ink-soft">
				Folders need DNS that can point your root domain at us — Cloudflare, AWS Route 53, or any provider with
				ALIAS/ANAME records. Subdomains work anywhere. Switching styles is coming soon — your translations will
				be kept and old addresses will keep redirecting.
			</p>
		</section>
	)
}

function ModePill({ selected, label, example }: { selected: boolean; label: string; example: string }) {
	return (
		<span
			className={
				'rounded-full border px-4 py-2 text-left ' +
				(selected ? 'border-ink bg-ink text-paper' : 'border-line text-ink-mute')
			}
		>
			<span className="text-[13px] font-medium">
				{label} {selected && '✓'}
			</span>
			<span className={'ml-2 font-mono text-[11px] ' + (selected ? 'text-paper/70' : 'text-ink-mute')}>
				{example}
			</span>
		</span>
	)
}

/* ============= Danger zone ============= */

function DangerCard({ website, routes, host }: { website: IWebsite; routes: IRoute[]; host: string }) {
	const navigate = useNavigate()
	const [confirming, setConfirming] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	async function deleteSite() {
		setDeleting(true)
		setError(null)
		try {
			await trpc().WebsiteService.Delete.mutate(website.id)
			navigate('/')
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to remove the site')
			setDeleting(false)
			setConfirming(false)
		}
	}

	// We can't reach into the owner's DNS, so removing the site leaves their
	// records behind pointing at nothing — naming them is the difference between
	// a clean removal and a mystery subdomain that 404s months later.
	const orphaned = routes.filter((r) => r.custom_hostname_id).map((r) => r.hostname)

	return (
		<section className="card border-[#B23B2A]/40 p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="font-sans text-[16px] font-medium text-[#B23B2A]">Remove this site</h2>
					<p className="mt-1 max-w-[46ch] text-[12px] text-ink-soft">
						Translations stop and every language address goes offline; your original site is untouched.
					</p>
				</div>
				<MiniButton onClick={() => setConfirming(true)}>Remove…</MiniButton>
			</div>

			{error && (
				<div className="mt-3 rounded-nayda-sm border border-[#B23B2A]/20 bg-[#B23B2A]/10 px-3 py-2 text-[13px] text-[#B23B2A]">
					{error}
				</div>
			)}

			{confirming && (
				<ConfirmDialog
					title={`Remove ${host}?`}
					body="Every language address goes offline and your translations are deleted. Your original site is untouched — this is the one thing here you can't undo."
					confirmWord={host}
					cancelLabel="Cancel"
					confirmLabel={deleting ? 'Removing…' : 'Remove site'}
					onCancel={() => setConfirming(false)}
					onConfirm={() => void deleteSite()}
				>
					{orphaned.length > 0 && (
						<div className="mt-4 rounded-nayda-sm border border-line bg-surface/50 p-3">
							<div className="text-[12px] text-ink-soft">
								These domain records become safe to delete at your registrar:
							</div>
							<ul className="mt-2 flex flex-col gap-1">
								{orphaned.map((hostname) => (
									<li
										key={hostname}
										className="break-all font-mono text-[11px] text-ink"
									>
										CNAME · {hostname}
									</li>
								))}
							</ul>
						</div>
					)}
				</ConfirmDialog>
			)}
		</section>
	)
}
